// ArbDashboard.jsx - ARB (Arbitration) Management Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const ArbDashboard = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState({
    openArbs: 0,
    resolvedThisMonth: 0,
    recoveredThisMonth: 0,
    approachingDeadline: 0
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [arbModal, setArbModal] = useState({ show: false, vehicle: null });
  const [resolveModal, setResolveModal] = useState({ show: false, vehicle: null });
  const [detailModal, setDetailModal] = useState({ show: false, vehicle: null });
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getArbDashboard(activeTab);
      setVehicles(data.vehicles || []);
      setStats(data.stats || {});
    } catch (err) {
      console.error('Failed to load ARB dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleArbitrate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const arbType = formData.get('arbType');
    const notes = formData.get('notes');
    
    if (!arbType) {
      alert('Please select an ARB type');
      return;
    }
    if (!notes?.trim()) {
      alert('Please describe the issue');
      return;
    }

    try {
      await api.updateArbAction(arbModal.vehicle.id, 'arbitrate', notes, { arbType });
      setArbModal({ show: false, vehicle: null });
      loadData();
    } catch (err) {
      alert('Failed to file ARB: ' + err.message);
    }
  };

  const handleResolve = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const arbOutcome = formData.get('arbOutcome');
    const arbRecoveryAmount = formData.get('arbRecoveryAmount') || 0;
    const notes = formData.get('notes');

    if (!arbOutcome) {
      alert('Please select an outcome');
      return;
    }

    try {
      await api.updateArbAction(resolveModal.vehicle.id, 'resolve', notes, { 
        arbOutcome, 
        arbRecoveryAmount: parseFloat(arbRecoveryAmount) || 0 
      });
      setResolveModal({ show: false, vehicle: null });
      loadData();
    } catch (err) {
      alert('Failed to resolve ARB: ' + err.message);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !detailModal.vehicle) return;
    setSavingNote(true);
    try {
      await api.updateVehicle(detailModal.vehicle.id, {
        arbNotes: detailModal.vehicle.arbNotes 
          ? `${detailModal.vehicle.arbNotes}\n\n[${new Date().toLocaleString()}]\n${newNote}`
          : `[${new Date().toLocaleString()}]\n${newNote}`
      });
      setNewNote('');
      loadData();
      // Update the modal with new notes
      const updatedVehicle = { 
        ...detailModal.vehicle, 
        arbNotes: detailModal.vehicle.arbNotes 
          ? `${detailModal.vehicle.arbNotes}\n\n[${new Date().toLocaleString()}]\n${newNote}`
          : `[${new Date().toLocaleString()}]\n${newNote}`
      };
      setDetailModal({ show: true, vehicle: updatedVehicle });
    } catch (err) {
      alert('Failed to add note: ' + err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      v.stockNumber?.toLowerCase().includes(searchLower) ||
      v.vin?.toLowerCase().includes(searchLower) ||
      v.make?.toLowerCase().includes(searchLower) ||
      v.model?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getDaysOpen = (filedDate) => {
    if (!filedDate) return '-';
    const filed = new Date(filedDate);
    const now = new Date();
    const diffTime = Math.abs(now - filed);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-neutral-500 hover:text-neutral-700">
              ← Back
            </button>
          )}
          <h1 className="text-2xl font-bold">ARB Management</h1>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-red-700">{stats.openArbs || 0}</div>
          <div className="text-sm text-red-600">Open ARBs</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-yellow-700">{stats.approachingDeadline || 0}</div>
          <div className="text-sm text-yellow-600">Approaching Deadline</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-green-700">{stats.resolvedThisMonth || 0}</div>
          <div className="text-sm text-green-600">Resolved This Month</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-blue-700">
            ${parseFloat(stats.recoveredThisMonth || 0).toLocaleString()}
          </div>
          <div className="text-sm text-blue-600">Recovered This Month</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-neutral-200">
        {[
          { id: 'all', label: 'All Vehicles' },
          { id: 'open', label: 'Open ARBs' },
          { id: 'resolved', label: 'Resolved' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium transition ${
              activeTab === tab.id
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by stock #, VIN, make, model..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Vehicle List */}
      {loading ? (
        <div className="text-center py-12 text-neutral-500">Loading...</div>
      ) : filteredVehicles.length === 0 ? (
        <div className="text-center py-12 text-neutral-500">
          {search ? 'No vehicles match your search' : 'No vehicles found'}
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-neutral-600">Vehicle</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600">ARB Deadline</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600">ARB Type</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600">Filed</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600">Outcome</th>
                <th className="text-right px-4 py-3 font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map(v => {
                const isInArb = v.inArbitration && v.arbStatus === 'arbitrated';
                const isResolved = v.arbStatus === 'resolved';
                const daysLeft = v.daysUntilDeadline;
                const isUrgent = daysLeft !== null && daysLeft <= 3 && !isInArb && !isResolved;
                
                return (
                  <tr 
                    key={v.id} 
                    onClick={() => setDetailModal({ show: true, vehicle: v })}
                    className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${
                    isInArb ? 'bg-purple-50' : isUrgent ? 'bg-red-50' : ''
                  }`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">
                        {v.year} {v.make} {v.model}
                      </div>
                      {v.structureDamageFound && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                          ⚠️ Structure Damage
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isInArb ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                          In Arbitration
                        </span>
                      ) : isResolved ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          Resolved
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full">
                          {v.status || 'Active'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {v.arbDeadline ? (
                        <div>
                          <div className={isUrgent ? 'text-red-600 font-medium' : ''}>
                            {formatDate(v.arbDeadline)}
                          </div>
                          {daysLeft !== null && !isResolved && (
                            <div className={`text-xs ${daysLeft <= 1 ? 'text-red-600' : daysLeft <= 3 ? 'text-yellow-600' : 'text-neutral-500'}`}>
                              {daysLeft < 0 ? 'Expired' : daysLeft === 0 ? 'Today!' : `${daysLeft} days left`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {v.arbType ? (
                        <span className={`px-2 py-1 text-xs rounded ${
                          v.arbType === 'Structure' ? 'bg-red-100 text-red-700' :
                          v.arbType === 'Mechanical' ? 'bg-orange-100 text-orange-700' :
                          'bg-neutral-100 text-neutral-600'
                        }`}>
                          {v.arbType}
                        </span>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {v.arbFiledDate ? (
                        <div>
                          <div>{formatDate(v.arbFiledDate)}</div>
                          {isInArb && (
                            <div className="text-xs text-neutral-500">
                              {getDaysOpen(v.arbFiledDate)} days open
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isResolved ? (
                        <div>
                          <div className={`font-medium ${
                            v.arbOutcome === 'Paid' ? 'text-green-600' :
                            v.arbOutcome === 'Partial' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {v.arbOutcome}
                          </div>
                          {v.arbRecoveryAmount > 0 && (
                            <div className="text-xs text-green-600">
                              ${parseFloat(v.arbRecoveryAmount).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isInArb ? (
                        <button
                          onClick={() => setResolveModal({ show: true, vehicle: v })}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Resolve
                        </button>
                      ) : !isResolved ? (
                        <button
                          onClick={() => setArbModal({ show: true, vehicle: v })}
                          className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                        >
                          Arbitrate
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Arbitrate Modal */}
      {arbModal.show && arbModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setArbModal({ show: false, vehicle: null })}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">File ARB Claim</h2>
              <p className="text-sm text-neutral-500">
                {arbModal.vehicle.stockNumber} - {arbModal.vehicle.year} {arbModal.vehicle.make} {arbModal.vehicle.model}
              </p>
            </div>
            <form onSubmit={handleArbitrate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">ARB Type *</label>
                <select name="arbType" className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white" required>
                  <option value="">Select type...</option>
                  <option value="Mechanical">Mechanical</option>
                  <option value="Structure">Structure</option>
                  <option value="Title">Title</option>
                  <option value="Odometer">Odometer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Issue Description *</label>
                <textarea
                  name="notes"
                  rows={4}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none"
                  placeholder="Describe the issue in detail..."
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setArbModal({ show: false, vehicle: null })}
                  className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  File ARB
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {resolveModal.show && resolveModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setResolveModal({ show: false, vehicle: null })}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">Resolve ARB</h2>
              <p className="text-sm text-neutral-500">
                {resolveModal.vehicle.stockNumber} - {resolveModal.vehicle.year} {resolveModal.vehicle.make} {resolveModal.vehicle.model}
              </p>
              {resolveModal.vehicle.arbType && (
                <p className="text-sm text-neutral-500">Type: {resolveModal.vehicle.arbType}</p>
              )}
            </div>
            <form onSubmit={handleResolve} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Outcome *</label>
                <select name="arbOutcome" className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white" required>
                  <option value="">Select outcome...</option>
                  <option value="Paid">Paid (Full Recovery)</option>
                  <option value="Partial">Partial Recovery</option>
                  <option value="Denied">Denied</option>
                  <option value="Withdrawn">Withdrawn</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Recovery Amount ($)</label>
                <input
                  type="number"
                  name="arbRecoveryAmount"
                  step="0.01"
                  min="0"
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none"
                  placeholder="Resolution notes..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setResolveModal({ show: false, vehicle: null })}
                  className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Resolve ARB
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ARB Detail Modal */}
      {detailModal.show && detailModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetailModal({ show: false, vehicle: null })}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="font-bold text-xl">ARB Case Details</h2>
                <p className="text-sm text-neutral-500">
                  {detailModal.vehicle.stockNumber} - {detailModal.vehicle.year} {detailModal.vehicle.make} {detailModal.vehicle.model}
                </p>
              </div>
              <button 
                onClick={() => setDetailModal({ show: false, vehicle: null })}
                className="text-neutral-400 hover:text-neutral-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Vehicle Info */}
              <div className="grid grid-cols-2 gap-4 bg-neutral-50 rounded-lg p-4">
                <div>
                  <div className="text-xs text-neutral-500">VIN</div>
                  <div className="font-mono text-sm">{detailModal.vehicle.vin || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Purchase Date</div>
                  <div className="text-sm">{detailModal.vehicle.purchaseDate ? formatDate(detailModal.vehicle.purchaseDate) : '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Auction</div>
                  <div className="text-sm">{detailModal.vehicle.auction || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Purchase Price</div>
                  <div className="text-sm font-medium">${parseFloat(detailModal.vehicle.purchasePrice || 0).toLocaleString()}</div>
                </div>
              </div>

              {/* ARB Status */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-3">ARB Status</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-red-600">Status</div>
                    <div className="font-medium">
                      {detailModal.vehicle.arbStatus === 'arbitrated' ? 'In Arbitration' : 
                       detailModal.vehicle.arbStatus === 'resolved' ? 'Resolved' : 
                       'Pending'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-red-600">ARB Type</div>
                    <div className="font-medium">{detailModal.vehicle.arbType || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-red-600">ARB Deadline</div>
                    <div className="font-medium">{detailModal.vehicle.arbDeadline ? formatDate(detailModal.vehicle.arbDeadline) : '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-red-600">Filed Date</div>
                    <div className="font-medium">{detailModal.vehicle.arbFiledDate ? formatDate(detailModal.vehicle.arbFiledDate) : '-'}</div>
                  </div>
                  {detailModal.vehicle.arbStatus === 'resolved' && (
                    <>
                      <div>
                        <div className="text-xs text-red-600">Outcome</div>
                        <div className="font-medium">{detailModal.vehicle.arbOutcome || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-red-600">Recovery Amount</div>
                        <div className="font-medium text-green-600">
                          ${parseFloat(detailModal.vehicle.arbRecoveryAmount || 0).toLocaleString()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {detailModal.vehicle.structureDamageFound && (
                  <div className="mt-3 px-3 py-2 bg-red-100 text-red-700 rounded text-sm">
                    Structure Damage Found
                  </div>
                )}
              </div>

              {/* Notes History */}
              <div>
                <h3 className="font-semibold mb-3">Notes & Updates</h3>
                <div className="bg-neutral-50 rounded-lg p-4 min-h-[100px] max-h-[200px] overflow-y-auto">
                  {detailModal.vehicle.arbNotes ? (
                    <pre className="whitespace-pre-wrap text-sm font-sans">{detailModal.vehicle.arbNotes}</pre>
                  ) : (
                    <p className="text-neutral-400 text-sm italic">No notes yet</p>
                  )}
                </div>
              </div>

              {/* Add Note */}
              <div>
                <h3 className="font-semibold mb-2">Add Update</h3>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note or update about this ARB case..."
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none h-24"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleAddNote}
                    disabled={savingNote || !newNote.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingNote ? 'Saving...' : 'Add Note'}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                {detailModal.vehicle.arbStatus === 'arbitrated' ? (
                  <button
                    onClick={() => {
                      setDetailModal({ show: false, vehicle: null });
                      setResolveModal({ show: true, vehicle: detailModal.vehicle });
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Resolve ARB
                  </button>
                ) : detailModal.vehicle.arbStatus !== 'resolved' ? (
                  <button
                    onClick={() => {
                      setDetailModal({ show: false, vehicle: null });
                      setArbModal({ show: true, vehicle: detailModal.vehicle });
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    File ARB Claim
                  </button>
                ) : null}
                <button
                  onClick={() => setDetailModal({ show: false, vehicle: null })}
                  className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArbDashboard;
